import { type FormEvent, type ReactNode, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FORUM_MAX_BODY_LENGTH, FORUM_MIN_BODY_LENGTH } from '@lms/shared-types';
import {
  formatForumDate,
  formatForumLastReply,
  forumPostAvatarInitials,
  forumVisibleAuthor,
  type ForumPost,
  type ForumThreadDetail,
} from '@/features/course-forum/types';

interface CourseForumThreadFlowProps {
  thread: ForumThreadDetail;
  openingPost?: ForumPost;
  replies: ForumPost[];
  backTo: string;
  staffView?: boolean;
  reply: string;
  replyError: string | null;
  replyPending?: boolean;
  onReplyChange: (value: string) => void;
  onReplySubmit: (event: FormEvent) => void;
  onQuote: (post: ForumPost) => void;
  replyTitle: string;
  replyPlaceholder: string;
  onSaveDraft?: () => void;
  draftSaved?: boolean;
  onTogglePin?: () => void;
  onToggleLock?: () => void;
  onDeleteThread?: () => void;
  onDeletePost?: (post: ForumPost) => void;
  actionsPending?: boolean;
}

const STAFF_TOOLS = [
  { icon: 'format_bold', label: 'Negrita' },
  { icon: 'format_italic', label: 'Cursiva' },
  { icon: 'format_underlined', label: 'Subrayado' },
  { icon: 'format_list_bulleted', label: 'Lista con viñetas' },
  { icon: 'format_list_numbered', label: 'Lista numerada' },
  { icon: 'format_quote', label: 'Cita' },
  { icon: 'link', label: 'Insertar enlace' },
  { icon: 'code', label: 'Código' },
  { icon: 'attach_file', label: 'Adjuntar recurso' },
] as const;

function avatarTone(id: string): string {
  let hash = 0;
  for (const char of id) hash += char.charCodeAt(0);
  return hash % 2 === 0 ? '' : ' lms-forum-flow__avatar--alt';
}

function StatusPill({ locked }: { locked: boolean }) {
  return (
    <span className={`lms-forum-flow__status${locked ? ' lms-forum-flow__status--closed' : ''}`}>
      <span className="lms-forum-flow__status-dot" aria-hidden="true" />
      {locked ? 'Cerrado' : 'Abierto'}
    </span>
  );
}

function AuthorIdentity({ post, staffView, showDate }: { post: ForumPost; staffView: boolean; showDate?: boolean }) {
  const identity = forumVisibleAuthor(post.author, staffView);
  const role = post.author.isStaff ? 'Docente' : 'Estudiante';

  return (
    <div className="lms-forum-flow__identity">
      <span className="lms-forum-flow__name">{identity.name}</span>
      {identity.alias ? (
        <>
          <span className="lms-forum-flow__sep" aria-hidden="true">
            •
          </span>
          <span className="lms-forum-flow__alias">{identity.alias}</span>
        </>
      ) : null}
      <span className={`lms-forum-flow__role${post.author.isStaff ? ' lms-forum-flow__role--staff' : ''}`}>{role}</span>
      {showDate ? (
        <>
          <span className="lms-forum-flow__sep" aria-hidden="true">
            •
          </span>
          <time className="lms-forum-flow__when" dateTime={post.createdAt}>
            {formatForumDate(post.createdAt)}
          </time>
        </>
      ) : null}
    </div>
  );
}

export function CourseForumThreadFlow({
  thread,
  openingPost,
  replies,
  backTo,
  staffView = false,
  reply,
  replyError,
  replyPending = false,
  onReplyChange,
  onReplySubmit,
  onQuote,
  replyTitle,
  replyPlaceholder,
  onSaveDraft,
  draftSaved = false,
  onTogglePin,
  onToggleLock,
  onDeleteThread,
  onDeletePost,
  actionsPending = false,
}: CourseForumThreadFlowProps) {
  const replyRef = useRef<HTMLTextAreaElement>(null);
  const [newestFirst, setNewestFirst] = useState(false);
  const orderedReplies = newestFirst ? [...replies].reverse() : replies;
  const lastReplyAt = replies.length > 0 ? replies[replies.length - 1]?.createdAt : thread.updatedAt;

  function quote(post: ForumPost) {
    onQuote(post);
    replyRef.current?.focus();
  }

  let opening: ReactNode = <p className="lms-forum-flow__missing">El mensaje original fue eliminado.</p>;
  if (openingPost) {
    opening = (
      <div className="lms-forum-flow__person">
        <div
          className={`lms-forum-flow__avatar${openingPost.author.isStaff ? ' lms-forum-flow__avatar--staff' : avatarTone(openingPost.id)}`}
          aria-hidden="true"
        >
          {forumPostAvatarInitials(openingPost)}
        </div>
        <div className="lms-forum-flow__col">
          <div className="lms-forum-flow__row">
            <AuthorIdentity post={openingPost} staffView={staffView} />
            <StatusPill locked={thread.isLocked} />
          </div>
          <div className="lms-forum-flow__bubble">{openingPost.body}</div>
          <footer className="lms-forum-flow__op-foot">
            <div className="lms-forum-flow__meta">
              <span className="lms-forum-flow__meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">
                  schedule
                </span>
                <time dateTime={openingPost.createdAt}>{formatForumDate(openingPost.createdAt)}</time>
              </span>
              <span className="lms-forum-flow__sep" aria-hidden="true">
                •
              </span>
              <span className="lms-forum-flow__meta-strong">
                {replies.length} {replies.length === 1 ? 'respuesta' : 'respuestas'}
              </span>
              <span className="lms-forum-flow__sep" aria-hidden="true">
                •
              </span>
              <span>{formatForumLastReply(lastReplyAt ?? thread.updatedAt, replies.length)}</span>
            </div>
            {onDeletePost ? (
              <button
                type="button"
                className="lms-forum-flow__delete"
                onClick={() => onDeletePost(openingPost)}
                disabled={actionsPending}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete_sweep
                </span>
                Eliminar mensaje
              </button>
            ) : null}
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div className="lms-forum-flow">
      <header className="lms-forum-flow__bar">
        <Link to={backTo} className="lms-forum-flow__title">
          <span className="material-symbols-outlined" aria-hidden="true">
            arrow_back
          </span>
          <span className="lms-forum-flow__title-text">{thread.title}</span>
          <StatusPill locked={thread.isLocked} />
          {thread.isPinned ? <span className="lms-course-forum-badge lms-course-forum-badge--pinned">Fijado</span> : null}
        </Link>
        {onTogglePin || onToggleLock || onDeleteThread ? (
          <div className="lms-forum-flow__actions">
            {onTogglePin ? (
              <button type="button" className="lms-forum-flow__action" onClick={onTogglePin} disabled={actionsPending}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  push_pin
                </span>
                {thread.isPinned ? 'Quitar fijado' : 'Fijar hilo'}
              </button>
            ) : null}
            {onToggleLock ? (
              <button
                type="button"
                className="lms-forum-flow__action lms-forum-flow__action--lock"
                onClick={onToggleLock}
                disabled={actionsPending}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  lock
                </span>
                {thread.isLocked ? 'Reabrir hilo' : 'Cerrar hilo'}
              </button>
            ) : null}
            {onDeleteThread ? (
              <button
                type="button"
                className="lms-forum-flow__action lms-forum-flow__action--danger"
                onClick={onDeleteThread}
                disabled={actionsPending}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
                Eliminar hilo
              </button>
            ) : null}
          </div>
        ) : null}
      </header>

      <article className="lms-forum-flow__op">{opening}</article>

      <section className="lms-forum-flow__replies">
        <div className="lms-forum-flow__replies-head">
          <h3>
            <span className="material-symbols-outlined" aria-hidden="true">
              forum
            </span>
            Respuestas en el hilo{' '}
            <span className="lms-forum-flow__count">
              ({replies.length} {replies.length === 1 ? 'aporte' : 'aportes'})
            </span>
          </h3>
          <button
            type="button"
            className="lms-forum-flow__sort"
            onClick={() => setNewestFirst((value) => !value)}
            disabled={replies.length < 2}
          >
            <span className="material-symbols-outlined" aria-hidden="true">
              sort
            </span>
            {newestFirst ? 'Más recientes' : 'Orden cronológico'}
          </button>
        </div>

        {orderedReplies.length === 0 ? (
          <p className="lms-forum-flow__empty">Aún no hay respuestas en este hilo.</p>
        ) : (
          <div className="lms-forum-flow__reply-list">
            {orderedReplies.map((post) => (
              <article key={post.id} className="lms-forum-flow__reply">
                <div className="lms-forum-flow__person">
                  <div
                    className={`lms-forum-flow__avatar${post.author.isStaff ? ' lms-forum-flow__avatar--staff' : avatarTone(post.id)}`}
                    aria-hidden="true"
                  >
                    {forumPostAvatarInitials(post)}
                  </div>
                  <div className="lms-forum-flow__col">
                    <div className="lms-forum-flow__row">
                      <AuthorIdentity post={post} staffView={staffView} showDate />
                      <span className="lms-forum-flow__post-id">ID: #{post.id.slice(-8)}</span>
                    </div>
                    <div className="lms-forum-flow__bubble">{post.body}</div>
                    <footer className="lms-forum-flow__reply-foot">
                      {thread.isLocked ? (
                        <span />
                      ) : (
                        <button type="button" className="lms-forum-flow__quote" onClick={() => quote(post)}>
                          <span className="material-symbols-outlined" aria-hidden="true">
                            reply
                          </span>
                          Citar respuesta
                        </button>
                      )}
                      {onDeletePost ? (
                        <button
                          type="button"
                          className="lms-forum-flow__delete"
                          onClick={() => onDeletePost(post)}
                          disabled={actionsPending}
                        >
                          <span className="material-symbols-outlined" aria-hidden="true">
                            delete
                          </span>
                          Eliminar mensaje
                        </button>
                      ) : null}
                    </footer>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {thread.isLocked ? (
        <p className="lms-forum-flow__locked">Este hilo está cerrado y no admite nuevas respuestas.</p>
      ) : (
        <form className="lms-forum-flow__composer" onSubmit={onReplySubmit}>
          <h3 className="lms-forum-flow__composer-title">
            {staffView ? (
              <span className="lms-forum-flow__composer-mark" aria-hidden="true">
                <span className="material-symbols-outlined">school</span>
              </span>
            ) : null}
            {replyTitle}
          </h3>
          <div className="lms-forum-flow__editor">
            {staffView ? (
              <div className="lms-forum-flow__tools" aria-hidden="true">
                {STAFF_TOOLS.map((tool, index) => (
                  <span key={tool.icon} className="lms-forum-flow__tool-slot">
                    {index === 3 || index === 6 ? <span className="lms-forum-flow__tool-sep" /> : null}
                    <span className="lms-forum-flow__tool" title={tool.label}>
                      <span className="material-symbols-outlined">{tool.icon}</span>
                    </span>
                  </span>
                ))}
              </div>
            ) : null}
            <textarea
              ref={replyRef}
              value={reply}
              onChange={(event) => onReplyChange(event.target.value)}
              minLength={FORUM_MIN_BODY_LENGTH}
              maxLength={FORUM_MAX_BODY_LENGTH}
              required
              rows={5}
              placeholder={replyPlaceholder}
              className="lms-forum-flow__textarea"
              aria-label={replyTitle}
            />
          </div>
          {replyError ? (
            <p className="lms-course-forum-modal__error" role="alert">
              {replyError}
            </p>
          ) : null}
          <div className="lms-forum-flow__composer-foot">
            {draftSaved ? <span className="lms-forum-flow__draft-note">Borrador guardado</span> : <span />}
            <div className="lms-forum-flow__composer-actions">
              {onSaveDraft ? (
                <button type="button" className="lms-forum-flow__draft" onClick={onSaveDraft} disabled={!reply.trim()}>
                  Guardar borrador
                </button>
              ) : null}
              <button type="submit" className="lms-forum-flow__send" disabled={replyPending}>
                <span className="material-symbols-outlined" aria-hidden="true">
                  send
                </span>
                {replyPending ? 'Enviando…' : 'Responder'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
